import AboutTemplate from "../../components/about/AboutTemplate";
import Page from "../Page";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import Section from "../../components/about/Section";
import DeveloperNotes from "../../components/about/DeveloperNotes";
import ResponsiveImage from "../../components/about/ResponsiveImage";

const DayPlanner = () => {
  return (
    <Page className="about-page day-planner-page" pageKey="day-planner">
      <AboutTemplate>
        <Typography variant="h1">Day Planner</Typography>

        <Section>
          <Typography variant="p1">
            The Day Planner is a visual scheduling tool that helps you plan out
            your day, block by block.
          </Typography>
          <Typography variant="p1">
            Instead of just having a list of tasks, the Day Planner lets you
            assign specific time slots to your tasks and activities, giving you
            a clear picture of how your day will unfold.
          </Typography>
          <ResponsiveImage
            pageKey="dayPlanner"
            imageName="day-planner-overview"
            size="large"
          />
        </Section>

        <Section title="How it works">
          <Typography variant="p1">
            Your day is divided into time slots based on your chosen interval
            (15 minutes, 30 minutes, or 1 hour). You can fill these slots with
            three types of blocks:
          </Typography>
          <Typography variant="p1">
            <strong>Time Blocks</strong> - General-purpose blocks for anything
            you want to schedule. Give them a name and a colour.{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner/time-blocks"
              variant="p1Link"
            >
              Learn more
            </Link>
          </Typography>
          <Typography variant="p1">
            <strong>Activity Blocks</strong> - Blocks linked to your{" "}
            <Link
              component={RouterLink}
              to="/about/features/activities"
              variant="p1Link"
            >
              activities
            </Link>
            . These automatically inherit the activity's name, emoji, and
            colour.{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner/activity-blocks"
              variant="p1Link"
            >
              Learn more
            </Link>
          </Typography>
          <Typography variant="p1">
            <strong>Task Blocks</strong> - Blocks linked to one or more tasks
            from your stack. Great for dedicating focused time to specific
            tasks.{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner/task-blocks"
              variant="p1Link"
            >
              Learn more
            </Link>
          </Typography>
          <ResponsiveImage
            pageKey="dayPlanner"
            imageName="block-types"
            size="large"
          />
        </Section>

        <Section title="Configuring your day">
          <Typography variant="p1">
            Before adding blocks, you can configure your day's start time, end
            time, and time interval. Click the settings icon at the top of the
            Day Planner to access the Day Configuration panel.
          </Typography>
          <Typography variant="p1">
            The <strong>start time</strong> and <strong>end time</strong> define
            the boundaries of your planning window. The{" "}
            <strong>time interval</strong> sets how granular your time slots are
            - choose 15-minute intervals for detailed planning, or 1-hour
            intervals for a higher-level overview.
          </Typography>
          <ResponsiveImage
            pageKey="dayPlanner"
            imageName="day-configuration"
            size="medium"
          />
        </Section>

        <Section title="Adding blocks">
          <Typography variant="p1">
            To add a block to your day, click on an empty time slot. This will
            open the Insert Block panel where you can choose your block type,
            set the time range, and configure the block's details.
          </Typography>
          <ResponsiveImage
            pageKey="dayPlanner"
            imageName="insert-block"
            size="medium"
          />
        </Section>

        <Section title="Navigating between days">
          <Typography variant="p1">
            Use the date navigator at the top of the Day Planner to move between
            days. You can plan ahead by navigating to future dates, or review
            past days to see what you accomplished.
          </Typography>
          <Typography variant="p1">
            Past time slots will appear dimmed, so you can easily see where you
            are in your current day.
          </Typography>
          <ResponsiveImage
            pageKey="dayPlanner"
            imageName="date-navigator"
            size="medium"
          />
        </Section>

        <Section title="Templates">
          <Typography variant="p1">
            If you follow a similar routine on certain days, you can save your
            day plan as a template and reuse it later.{" "}
            <Link
              component={RouterLink}
              to="/about/features/day-planner/templates"
              variant="p1Link"
            >
              Learn more about templates
            </Link>
          </Typography>
        </Section>

        <DeveloperNotes>
          <Typography variant="p1">
            I built the Day Planner because I found that having a task list
            alone wasn't enough - I needed to know <em>when</em> I was going to
            do things, not just <em>what</em> I needed to do.
          </Typography>
          <Typography variant="p1">
            I find it most useful to plan my day first thing in the morning. I
            look at my task stack, pick out the things I want to focus on, and
            slot them into my day alongside my regular activities.
          </Typography>
          <Typography variant="p1">
            Don't worry about sticking to the plan perfectly - the value is in
            the act of planning itself. It gives you a sense of direction and
            helps you stay intentional about how you spend your time.
          </Typography>
        </DeveloperNotes>
      </AboutTemplate>
    </Page>
  );
};

export default DayPlanner;
